// src/pages/OrderSuccess.jsx — v11
// CHANGED FROM v10 (19 Jul 2026): /loyalty renamed to /cashback-program
// sitewide — this file's "Did you know?" link updated to match.
// CHANGED FROM v9 (18 Jul 2026): GST invoice feature reverted per
// Amit's call — parked for a future session. Back to v8 behavior
// exactly.
// CHANGED FROM v7: added a genuine thank-you line and a "did you know"
// section below the receipt card, highlighting nb wallet cashback,
// return policy, and the reseller programme — using the extra space
// on this screen better per Amit's request. No other logic changed;
// receipt generation still uses the shared utils/receipt.js.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { pixelPurchase } from '../utils/pixel';
import { generateReceiptImage, downloadBlob } from '../utils/receipt';
export default function OrderSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [orderDate] = useState(() => new Date());
  const [receiptBusy, setReceiptBusy] = useState(false);
  useEffect(() => {
    const stored = sessionStorage.getItem('nbs_order');
    if (stored) {
      const parsed = JSON.parse(stored);
      setOrder(parsed);
      pixelPurchase(parsed.orderId, parsed.total, parsed.cart || []);
    }
    sessionStorage.removeItem('nbs_order');
    sessionStorage.removeItem('nbs_order_payment');
  }, []);
  const orderId = order?.orderId || searchParams.get('order_id') || 'NBS-' + Date.now();
  const total = order?.total || 0;
  const address = order?.address || {};
  const cartItems = order?.cart || [];
  const dateStr = orderDate.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  async function handleShareReceipt() {
    setReceiptBusy(true);
    try {
      const blob = await generateReceiptImage({ orderId, dateStr, total, cart: cartItems });
      const file = new File([blob], `${orderId}-receipt.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'nobrand store — Order Receipt',
          text: `Order ${orderId} — Rs. ${Number(total).toLocaleString('en-IN')}`,
        });
      } else {
        downloadBlob(blob, `${orderId}-receipt.png`);
      }
    } catch (err) {
      // User cancelled share, or share unsupported — no action needed
    } finally {
      setReceiptBusy(false);
    }
  }
  async function handleDownloadReceipt() {
    setReceiptBusy(true);
    try {
      const blob = await generateReceiptImage({ orderId, dateStr, total, cart: cartItems });
      downloadBlob(blob, `${orderId}-receipt.png`);
    } finally {
      setReceiptBusy(false);
    }
  }
  const STEPS = [
    { label: 'Order confirmed', sub: 'Just now', done: true },
    { label: 'Packed & dispatched', sub: 'Expected by tomorrow', done: false },
    { label: 'Out for delivery', sub: '2–4 business days', done: false },
    { label: 'Delivered', sub: 'Estimated in 4 days', done: false },
  ];
  return (
    <div className="app-shell">
      <nav style={{ background: 'var(--navy)', padding: '14px 18px', display: 'flex', justifyContent: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--off-white)', letterSpacing: '0.18em', textTransform: 'uppercase' }}>nobrand</span>
      </nav>
      <div style={{ background: 'var(--navy)', padding: '32px 18px 40px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, background: 'var(--blue)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <i className="ti ti-check" style={{ fontSize: 32, color: '#fff' }}></i>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 500, color: 'var(--off-white)', marginBottom: 8 }}>Order confirmed!</h1>
        <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
          Thank you. Your order has been placed<br />and is being prepared for dispatch.
        </p>
        <div style={{ display: 'inline-block', marginTop: 14, background: 'rgba(244,242,238,0.1)', color: 'var(--gold)', fontSize: 12, padding: '6px 16px', borderRadius: 4, letterSpacing: '0.1em' }}>
          {orderId}
        </div>
      </div>
      <div className="page-body">
        <div style={{ background: '#FEF8E8', borderRadius: 10, padding: '16px 18px', marginBottom: 16, textAlign: 'center' }}>
          <i className="ti ti-heart" style={{ fontSize: 20, color: 'var(--gold)', marginBottom: 8, display: 'block' }}></i>
          <div style={{ fontSize: 13, color: '#5A4416', lineHeight: 1.6 }}>
            Thank you for shopping small with us — every order means a lot to our team. We can't wait for this one to reach you.
          </div>
        </div>
        {total > 0 && (
          <div className="card">
            <div className="section-title" style={{ marginBottom: 12 }}>Amount paid</div>
            <div style={{ fontSize: 24, fontWeight: 500, color: 'var(--navy)' }}>₹{total.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 4 }}>Payment successful via Razorpay</div>
          </div>
        )}
        {cartItems.length > 0 && (
          <div className="card">
            <div className="section-title" style={{ marginBottom: 12 }}>Order details</div>
            {cartItems.map(item => {
              const lineTotal = (Number(item.price) || 0) * (Number(item.qty) || 1);
              return (
                <div key={item.key || item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '0.5px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)' }}>
                      {item.id}{item.colorCode ? ` · ${item.colorCode}` : ''}{item.size ? ` · ${item.size}` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>Qty {item.qty} × ₹{Number(item.price).toLocaleString()}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)' }}>₹{lineTotal.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        )}
        <div className="card">
          <div className="section-title" style={{ marginBottom: 10 }}>Order receipt</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
            Save or share this receipt for your records — useful if you ever need to reference this order or payment with us.
          </div>
          <button
            className="btn-primary"
            style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={handleShareReceipt}
            disabled={receiptBusy}
          >
            <i className="ti ti-share"></i>
            {receiptBusy ? ' Preparing…' : ' Share receipt on WhatsApp'}
          </button>
          <button
            className="btn-outline"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={handleDownloadReceipt}
            disabled={receiptBusy}
          >
            <i className="ti ti-download"></i>
            Download receipt
          </button>
        </div>
        <div className="card">
          <div className="section-title" style={{ marginBottom: 14 }}>Did you know?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Link to="/cashback-program" style={{ display: 'flex', gap: 12, textDecoration: 'none' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#FEF8E8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="ti ti-gift" style={{ fontSize: 18, color: 'var(--gold)' }}></i>
              </div>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--navy)', marginBottom: 2 }}>You're earning nb wallet cashback</div>
                <div style={{ fontSize: 11.5, color: '#666', lineHeight: 1.5 }}>Cashback on this order lands in your wallet a day after delivery is confirmed — use it on any future order.</div>
              </div>
            </Link>
            <Link to="/returns" style={{ display: 'flex', gap: 12, textDecoration: 'none' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#EEF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="ti ti-rotate" style={{ fontSize: 18, color: 'var(--blue)' }}></i>
              </div>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--navy)', marginBottom: 2 }}>Free returns on eligible items</div>
                <div style={{ fontSize: 11.5, color: '#666', lineHeight: 1.5 }}>Wrong size, wrong color, or damaged? We arrange free pickup — you never pay for return shipping.</div>
              </div>
            </Link>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="ti ti-users" style={{ fontSize: 18, color: 'var(--green)' }}></i>
              </div>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--navy)', marginBottom: 2 }}>Turn your network into income</div>
                <div style={{ fontSize: 11.5, color: '#666', lineHeight: 1.5 }}>Share products from any product page and keep the margin you set — no stock, no investment. Look for "Share & Earn."</div>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="section-title" style={{ marginBottom: 14 }}>Delivery status</div>
          {STEPS.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: step.done ? 'var(--blue)' : 'rgba(27,42,74,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`ti ${step.done ? 'ti-check' : 'ti-point'}`} style={{ fontSize: 11, color: step.done ? '#fff' : 'var(--muted)' }}></i>
                </div>
                {i < STEPS.length - 1 && <div style={{ width: 1, flex: 1, background: 'var(--border)', margin: '2px 0', minHeight: 20 }}></div>}
              </div>
              <div style={{ paddingBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)', marginBottom: 2 }}>{step.label}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{step.sub}</div>
              </div>
            </div>
          ))}
        </div>
        {address.name && (
          <div className="card">
            <div className="section-title" style={{ marginBottom: 10 }}>Delivering to</div>
            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.8 }}>
              <div style={{ fontWeight: 500, color: 'var(--navy)', marginBottom: 4 }}>{address.name}</div>
              {address.flat}<br />
              {address.street && <>{address.street}<br /></>}
              {address.landmark && <>Near {address.landmark}<br /></>}
              {address.city}, {address.state} — {address.pin}<br />
              {address.phone}
            </div>
          </div>
        )}
        <button className="btn-primary" style={{ marginBottom: 10 }} onClick={() => navigate('/account/orders')}>View my orders</button>
        <button className="btn-outline" onClick={() => navigate('/shop')}>Continue shopping</button>
      </div>
    </div>
  );
}